/**
 * RecordatorioModal — crear o editar un recordatorio personal.
 *
 * Si recibe `expediente`, ofrece selector de contactos (asegurado /
 * administrador / presidente / reparadores del expediente) que
 * autocompletan teléfono y nombre.
 */
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Bell, Calendar as CalendarIcon, Phone, User, ArrowsClockwise } from "@phosphor-icons/react";

const PRIORIDADES = [
  { k: "alta", label: "Alta", color: "bg-rose-100 text-rose-800 border-rose-200" },
  { k: "media", label: "Media", color: "bg-amber-100 text-amber-800 border-amber-200" },
  { k: "baja", label: "Baja", color: "bg-zinc-100 text-zinc-700 border-zinc-200" },
];

const TIPOS_CONTACTO = [
  { k: "reparador", label: "Reparador" },
  { k: "administrador", label: "Administrador" },
  { k: "asegurado", label: "Asegurado" },
  { k: "presidente", label: "Presidente" },
  { k: "otro", label: "Otro" },
];

const RECURRENCIAS = [
  { k: "none", label: "No se repite" },
  { k: "daily", label: "Cada día" },
  { k: "weekly", label: "Cada semana" },
  { k: "monthly", label: "Cada mes" },
];

function toDatetimeLocal(iso) {
  if (!iso) {
    // Por defecto: ahora + 1h, redondeado al cuarto
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setMinutes(Math.ceil(d.getMinutes() / 15) * 15);
    d.setSeconds(0);
    d.setMilliseconds(0);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  }
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export default function RecordatorioModal({ existing, expediente, onClose, onSaved }) {
  const isEdit = !!existing;
  const [titulo, setTitulo] = useState(existing?.titulo || "");
  const [descripcion, setDescripcion] = useState(existing?.descripcion || "");
  const [fechaHora, setFechaHora] = useState(toDatetimeLocal(existing?.fecha_hora));
  const [prioridad, setPrioridad] = useState(existing?.prioridad || "media");
  const [tipoContacto, setTipoContacto] = useState(existing?.tipo_contacto || "otro");
  const [telefono, setTelefono] = useState(existing?.telefono || "");
  const [contactoNombre, setContactoNombre] = useState(existing?.contacto?.nombre || "");
  const [contactoRefId, setContactoRefId] = useState(existing?.contacto?.ref_id || "");
  const [recurrencia, setRecurrencia] = useState(existing?.recurrencia || "none");
  const [recurrenciaUntil, setRecurrenciaUntil] = useState(existing?.recurrencia_until || "");
  const [submitting, setSubmitting] = useState(false);

  // Contactos disponibles del expediente (asegurado, admin, presidente, reparadores)
  const contactos = useMemo(() => {
    if (!expediente) return [];
    const ed = expediente.extracted_data || {};
    const list = [];
    if (ed.asegurado_nombre) {
      list.push({
        kind: "asegurado", ref_id: "",
        label: `Asegurado · ${ed.asegurado_nombre}`,
        nombre: ed.asegurado_nombre,
        telefono: ed.asegurado_movil || ed.asegurado_telefono || "",
      });
    }
    if (ed.administrador_nombre || ed.administrador_telefono) {
      list.push({
        kind: "administrador", ref_id: "",
        label: `Administrador · ${ed.administrador_nombre || "(sin nombre)"}`,
        nombre: ed.administrador_nombre || "Administrador",
        telefono: ed.administrador_telefono || "",
      });
    }
    if (ed.presidente_nombre || ed.presidente_telefono) {
      list.push({
        kind: "presidente", ref_id: "",
        label: `Presidente · ${ed.presidente_nombre || "(sin nombre)"}`,
        nombre: ed.presidente_nombre || "Presidente",
        telefono: ed.presidente_telefono || "",
      });
    }
    for (const r of (expediente.reparadores || [])) {
      const tel = (r.telefonos && r.telefonos[0]) || r.telefono || "";
      const nombre = r.empresa || r.nombre || "Reparador";
      list.push({
        kind: "reparador", ref_id: r.id,
        label: `Reparador · ${nombre}`,
        nombre, telefono: tel,
      });
    }
    for (const p of (expediente.implicados || [])) {
      if (!p.nombre) continue;
      const tel = p.telefono || p.movil || "";
      list.push({
        kind: "otro", ref_id: p.id,
        label: `Implicado · ${p.nombre}`,
        nombre: p.nombre, telefono: tel,
      });
    }
    return list;
  }, [expediente]);

  const onContactoSelect = (idx) => {
    if (idx === "" || idx === "-1") {
      setContactoNombre("");
      setContactoRefId("");
      return;
    }
    const c = contactos[Number(idx)];
    if (!c) return;
    setContactoNombre(c.nombre || "");
    setContactoRefId(c.ref_id || "");
    if (c.telefono) setTelefono(c.telefono);
    // Si el "tipo de contacto" del recordatorio aún es "otro" y el
    // seleccionado lo tiene definido, lo sincronizamos para facilitar.
    if (tipoContacto === "otro" && c.kind && c.kind !== "otro") {
      setTipoContacto(c.kind);
    }
    if (!titulo.trim()) {
      setTitulo(`Llamar a ${c.nombre || ""}`.trim());
    }
  };

  // Auto-seleccionar contacto coincidente al editar
  useEffect(() => {
    if (!existing?.contacto?.nombre || contactos.length === 0) return;
    const i = contactos.findIndex(
      (c) => c.nombre === existing.contacto.nombre && (c.ref_id || "") === (existing.contacto.ref_id || ""),
    );
    if (i >= 0) setContactoRefId(contactos[i].ref_id || "");
  }, [contactos, existing]);

  const submit = async () => {
    if (!titulo.trim()) {
      toast.error("Indica un título");
      return;
    }
    if (!fechaHora) {
      toast.error("Indica fecha y hora");
      return;
    }
    setSubmitting(true);
    try {
      // datetime-local → ISO UTC
      const localDate = new Date(fechaHora);
      const iso = localDate.toISOString();
      const payload = {
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || null,
        fecha_hora: iso,
        prioridad,
        tipo_contacto: tipoContacto,
        telefono: telefono.trim() || null,
        contacto: contactoNombre.trim()
          ? { kind: tipoContacto, ref_id: contactoRefId || null, nombre: contactoNombre.trim(), telefono: telefono.trim() || null }
          : null,
        recurrencia,
        recurrencia_until: recurrencia !== "none" && recurrenciaUntil ? recurrenciaUntil : null,
      };
      if (!isEdit) payload.expediente_id = expediente?.id || null;

      let resp;
      if (isEdit) {
        resp = await api.patch(`/recordatorios/${existing.id}`, payload);
      } else {
        resp = await api.post(`/recordatorios`, payload);
      }
      toast.success(isEdit ? "Recordatorio actualizado" : "Recordatorio creado");
      onSaved?.(resp.data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="recordatorio-modal"
    >
      <div
        className="bg-white border border-border rounded-xl w-full max-w-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-4 border-b border-border flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-amber-50 text-amber-700 border border-amber-200 flex items-center justify-center">
            <Bell className="w-4.5 h-4.5" weight="duotone" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="font-display font-bold text-[15px]">
              {isEdit ? "Editar recordatorio" : "Nuevo recordatorio"}
            </h2>
            {expediente && (
              <p className="text-[11.5px] text-muted truncate">
                Vinculado a {expediente?.extracted_data?.numero_siniestro || "expediente"}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-foreground"
            data-testid="recordatorio-modal-close"
          >
            <X className="w-5 h-5" weight="bold" />
          </button>
        </header>

        <div className="px-5 py-4 space-y-3.5">
          <div>
            <label className="text-[11.5px] font-semibold text-muted mb-1 block">Título</label>
            <input
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              autoFocus={!isEdit}
              placeholder="Llamar al fontanero, hablar con admin de fincas..."
              data-testid="recordatorio-titulo"
              className="w-full border border-border rounded-md h-9 px-2.5 text-[13px]"
            />
          </div>

          {contactos.length > 0 && (
            <div>
              <label className="text-[11.5px] font-semibold text-muted mb-1 block">
                Vincular contacto del expediente
              </label>
              <select
                onChange={(e) => onContactoSelect(e.target.value)}
                value={contactos.findIndex(
                  (c) => c.nombre === contactoNombre && (c.ref_id || "") === (contactoRefId || ""),
                )}
                data-testid="recordatorio-contacto-select"
                className="w-full border border-border rounded-md h-9 px-2 text-[12.5px] bg-white"
              >
                <option value="-1">— Sin vincular —</option>
                {contactos.map((c, i) => (
                  <option key={`${c.kind}-${c.ref_id || i}`} value={i}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11.5px] font-semibold text-muted mb-1 block">
                <CalendarIcon className="w-3 h-3 inline mr-1" weight="bold" /> Cuándo
              </label>
              <input
                type="datetime-local"
                value={fechaHora}
                onChange={(e) => setFechaHora(e.target.value)}
                data-testid="recordatorio-fecha-hora"
                className="w-full border border-border rounded-md h-9 px-2 text-[12.5px]"
              />
            </div>
            <div>
              <label className="text-[11.5px] font-semibold text-muted mb-1 block">Prioridad</label>
              <div className="flex gap-1">
                {PRIORIDADES.map((p) => (
                  <button
                    key={p.k}
                    type="button"
                    onClick={() => setPrioridad(p.k)}
                    data-testid={`recordatorio-prioridad-${p.k}`}
                    className={`flex-1 text-[11px] font-semibold px-1.5 py-1.5 rounded-md border transition-colors ${
                      prioridad === p.k ? p.color + " ring-2 ring-offset-1 ring-foreground/20" : "bg-white text-muted border-border hover:bg-zinc-50"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11.5px] font-semibold text-muted mb-1 block">
                <User className="w-3 h-3 inline mr-1" weight="bold" /> Tipo
              </label>
              <select
                value={tipoContacto}
                onChange={(e) => setTipoContacto(e.target.value)}
                data-testid="recordatorio-tipo-contacto"
                className="w-full border border-border rounded-md h-9 px-2 text-[12.5px] bg-white"
              >
                {TIPOS_CONTACTO.map((t) => (
                  <option key={t.k} value={t.k}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[11.5px] font-semibold text-muted mb-1 block">
                <Phone className="w-3 h-3 inline mr-1" weight="bold" /> Teléfono
              </label>
              <input
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="600 123 456"
                data-testid="recordatorio-telefono"
                className="w-full border border-border rounded-md h-9 px-2.5 text-[12.5px]"
              />
            </div>
          </div>

          <div>
            <label className="text-[11.5px] font-semibold text-muted mb-1 block">Nota (opcional)</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              rows={2}
              placeholder="Recordar preguntar por el presupuesto del salón..."
              data-testid="recordatorio-descripcion"
              className="w-full border border-border rounded-md px-2.5 py-1.5 text-[12.5px] resize-none"
            />
          </div>

          <div>
            <label className="text-[11.5px] font-semibold text-muted mb-1 block">
              <ArrowsClockwise className="w-3 h-3 inline mr-1" weight="bold" /> Recurrencia
            </label>
            <div className="flex gap-2">
              <select
                value={recurrencia}
                onChange={(e) => setRecurrencia(e.target.value)}
                data-testid="recordatorio-recurrencia"
                className="flex-1 border border-border rounded-md h-9 px-2 text-[12.5px] bg-white"
              >
                {RECURRENCIAS.map((r) => (
                  <option key={r.k} value={r.k}>{r.label}</option>
                ))}
              </select>
              {recurrencia !== "none" && (
                <input
                  type="date"
                  value={recurrenciaUntil}
                  onChange={(e) => setRecurrenciaUntil(e.target.value)}
                  placeholder="Hasta"
                  title="Repetir hasta esta fecha (opcional)"
                  data-testid="recordatorio-recurrencia-until"
                  className="flex-1 border border-border rounded-md h-9 px-2 text-[12.5px]"
                />
              )}
            </div>
          </div>
        </div>

        <footer className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-zinc-50">
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-[12.5px] font-semibold px-3 py-1.5 rounded-md border border-border bg-white hover:bg-zinc-100"
            data-testid="recordatorio-cancel"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="recordatorio-submit"
            className="text-[12.5px] font-semibold px-3 py-1.5 rounded-md bg-foreground text-white hover:bg-foreground/90 disabled:opacity-50"
          >
            {submitting ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear recordatorio"}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
